import { GAME_HAND, GAME_HAND_PHASE, HAND_TYPE, months } from 'constants/gameConstants';
import Decimal from 'decimal.js';
import pako from 'pako';
import { Player } from 'reducers/currentTable';
import { recordDecompress } from 'utils/perfCounters';

export function isEmpty(obj: object) {
  return Object.keys(obj).length === 0;
}

export function removeDuplicates(arr: any[]) {
  return arr.filter((item, index) => arr.indexOf(item) === index);
}

/**
 * @param gotLobbyResponse Raw data received on 'gotLobby' socket event
 * @returns array[]
 */
export function newLobbySchema(gotLobbyResponse: any) {
  const array_version_of_raw_data = Object.values(gotLobbyResponse.response);

  const final = array_version_of_raw_data.map((data: any) => {
    if (!isEmpty(data.games)) {
      data.games = Object.values(data.games);
    } else {
      data.games = Object.values(data.games);
    }

    return data;
  });

  return final;
}

// Cached device id ('_socketuuid' cookie). Reading document.cookie is a
// synchronous IPC call in the Android WebView, and GameService's baseParams
// useMemo re-derived it on every deps churn across every mounted panel -
// profiled at ~180 cookie reads (tens of ms of pure IPC) across one fast
// table-swipe loop. The id is stable for a session; the short TTL only
// bounds staleness if the socket layer ever rotates it.
const DEVICE_ID_TTL_MS = 5_000;
let cachedDeviceId: { value: string; at: number } | null = null;

export function getDeviceId(): string {
  const now = Date.now();
  if (cachedDeviceId && now - cachedDeviceId.at < DEVICE_ID_TTL_MS) return cachedDeviceId.value;
  const value = getCookie('_socketuuid');
  cachedDeviceId = { value, at: now };
  return value;
}

export function getCookie(cookieName: string) {
  const name = cookieName + '=';
  const cookieArray = document.cookie.split(';');
  for (let i = 0; i < cookieArray.length; i++) {
    let c = cookieArray[i];
    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
}

export function extractIDTokenFromDescopeCookie(descopeCookie: string): string {
  const descopeTokenObj = cookieCleanUp(descopeCookie);
  return descopeTokenObj?.id_token;
}

export function extractAccessTokenFromDescopeCookie(descopeCookie: string): string {
  const descopeTokenObj = cookieCleanUp(descopeCookie);
  return descopeTokenObj?.access_token;
}

export function extractRefreshTokenFromDescopeCookie(descopeCookie: string): string {
  const descopeTokenObj = cookieCleanUp(descopeCookie);
  return descopeTokenObj?.refresh_token;
}

function cookieCleanUp(cookie: string): any {
  return JSON.parse(
    decodeURIComponent(cookie)?.replace(/\\054/g, ',')?.replace(/\s/g, '')?.replace(/\\"/g, '"')
  );
}
export function groupBy(array: any, groupingField: string) {
  const groupCategoriesObject = array.reduce((group: any, game: any) => {
    const category = game[groupingField];
    group[category] = group[category] ?? [];
    group[category].push(game);
    return group;
  }, {});

  return groupCategoriesObject;
}

export function sortData(rowData: any, sortingColumn: string, sortingDirection: string) {
  if (sortingColumn === null) {
    return rowData;
  }

  return rowData.slice().sort((a: any, b: any) => {
    let aValue = a[sortingColumn];
    let bValue = b[sortingColumn];

    // Function to parse currency values and handle 'K'
    const parseCurrency = (value: string) => {
      if (value.includes('K')) {
        return parseFloat(value.replace(/\$/g, '').replace('K', '')) * 1000;
      }
      return parseFloat(value.replace(/\$/g, ''));
    };

    // Handle Stake column by splitting into small blind and big blind
    if (sortingColumn === 'stakes') {
      const parseStake = (value: string | number) => {
        if (typeof value === 'string') {
          const [smallBlind, bigBlind] = value.replace(/\$/g, '').split('/');
          return {
            smallBlind: parseCurrency(smallBlind),
            bigBlind: parseCurrency(bigBlind)
          };
        }
        return { smallBlind: 0, bigBlind: 0 }; // Return default values if not string
      };

      const aStake = parseStake(aValue);
      const bStake = parseStake(bValue);

      // Sort by big blind first, then small blind if big blinds are equal
      if (aStake.bigBlind === bStake.bigBlind) {
        aValue = aStake.smallBlind;
        bValue = bStake.smallBlind;
      } else {
        aValue = aStake.bigBlind;
        bValue = bStake.bigBlind;
      }
    } else if (sortingColumn === 'tablelimit' || sortingColumn === 'tablelow') {
      // Handle Limit and Tablelow columns by removing dollar sign, handling 'K', and converting to number
      aValue = parseCurrency(aValue);
      bValue = parseCurrency(bValue);
    }

    // If the values are equal, return 0 (no sorting needed)
    if (aValue === bValue) {
      return 0;
    }

    // Sort in ascending or descending order
    if (sortingDirection === 'asc') {
      return aValue < bValue ? -1 : 1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });
}

export function currencyFormatter(val: number | string) {
  return convertToNumber(val).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD'
  });
}

/** Currency format that drops .00 for whole numbers — e.g. $3 instead of $3.00 */
export function currencyCompact(val: number | string) {
  const str = String(val);
  // Handle pre-formatted strings with k/m suffix (e.g. "1.00k", "$5.00k")
  const suffixMatch = str.match(/^[$]?([\d.,]+)([kKmM])$/);
  if (suffixMatch) {
    const num = parseFloat(suffixMatch[1].replace(/,/g, ''));
    const suffix = suffixMatch[2];
    const formatted = Number.isInteger(num) ? `$${num}` : `$${num}`;
    return formatted.replace(/\.00$/, '') + suffix;
  }
  const n = convertToNumber(val);
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2
  });
}

export function numberFormatter(val: number | string) {
  return convertToNumber(val).toLocaleString('en-US');
}

/**
 * Money rendering for the in-game table: `$` prefix only on cash tables (tabletype 's').
 * Tournament tables ('t', 'm') and any unset/empty tabletype render the number alone.
 * Why: legacy code repeated this conditional in 5+ render sites and the gap let
 * `$` leak through when tabletype was uninitialized — this centralizes the rule.
 */
export function tableMoneyFormatter(val: number | string, tabletype?: string) {
  return (tabletype === 's' ? '$' : '') + numberFormatter(val);
}

/**
 * Coerce a possibly-string / possibly-currency-formatted amount into a number.
 * Server sometimes sends chip / bet values as comma-grouped strings like
 * `"100,000"` or with a `$` prefix; `Number()` of those is NaN. Strip
 * everything that isn't a digit / dot / sign before coercing; fall back to 0.
 * Used by compactChips + tableMoneyFormatterCompact below.
 */
export function toChips(val: number | string): number {
  const n = typeof val === 'string' ? Number(val.replace(/[^0-9.-]/g, '')) : Number(val);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Abbreviate a chip / money amount to K / M form: 100000 → "100K",
 * 1_500_000 → "1.50M", small values pass through with at most 2 decimal
 * places. Used for tournament blind readouts (see TournamentTableDetails)
 * and by tableMoneyFormatterCompact for in-table stacks / bets so
 * deep-stack levels don't overflow the seat pill or grid cell.
 *
 * Two rules:
 *   1. Truncate toward zero — a chip display must NEVER overstate the
 *      player's stack. 4,908 chips render as "4.90K", never "4.91K"; 6.999
 *      as "6.99", never "7".
 *   2. Fractional values render with exactly 2 decimals (padded with zeros
 *      if needed): "4.90K", "1.50M", "6.30". Whole numbers stay whole:
 *      "100K", "5", "0".
 *
 * Truncation happens in the STRING form (`toFixed(6)` + slice) because
 * `x * 100` re-introduces IEEE noise mid-flight (`0.29 * 100` is `28.999…`),
 * which naive `Math.trunc(x * 100) / 100` would render as `0.28` — a cent
 * short of the honest value.
 */
export function compactChips(val: number | string): string {
  const n = toChips(val);
  const abs = Math.abs(n);
  const format = (x: number): string => {
    const [intPart, fracPart = ''] = x.toFixed(6).split('.');
    const truncatedFrac = fracPart.slice(0, 2);
    const value = Number(truncatedFrac ? `${intPart}.${truncatedFrac}` : intPart);
    // toFixed(2) here PADS but never rounds up: `value` already has at most
    // 2 decimals from the slice above, so the rounding it does is a no-op.
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  };
  if (abs >= 1_000_000) return `${format(n / 1_000_000)}M`;
  if (abs >= 1_000) return `${format(n / 1_000)}K`;
  return format(n);
}

/**
 * `sb/bb (ante)` with each value abbreviated. Kept next to `compactChips` /
 * `toChips` so tournament level readouts and stack / bet displays share a
 * single truncation source.
 */
export function formatBlinds(sb: any, bb: any, ante: any): string {
  return `${compactChips(sb)}/${compactChips(bb)} (${compactChips(ante)})`;
}

/**
 * Compact money rendering for the in-game table. K / M abbreviation is only
 * applied on TOURNAMENT tables — deep-stack tourney chips would otherwise
 * overflow the seat pill / grid cell. Cash tables (`tabletype === 's'`) fall
 * through to the full comma-grouped format via tableMoneyFormatter, so a
 * $4,908 stack reads as "$4,908" (not "$4.90K"): cash stacks don't grow into
 * the millions, and the abbreviated form obscures the exact dollar amount
 * players want to see.
 *
 * Reach for this at render sites that render on BOTH cash and tournament
 * tables (Bet, Pot, Cash, SeatCardPill, PortraitCurrentPlayer). Sites that
 * are already tournament-only (TournamentDetails, TournamentTableDetails,
 * TableDetails' tournament branch) call `compactChips` directly.
 */
export function tableMoneyFormatterCompact(val: number | string, tabletype?: string) {
  if (tabletype === 's') return tableMoneyFormatter(val, tabletype);
  return compactChips(val);
}

export function getCardValue(card: string) {
  // Remove suit
  let value: any = card?.substring(0, card.length - 1);

  // Turn letters into numbers
  if (isNaN(value)) {
    switch (value) {
      case 'A':
        // Ace
        value = 14;
        break;
      case 'K':
        value = 13;
        break;
      case 'Q':
        value = 12;
        break;
      case 'J':
        value = 11;
    }
  } else {
    value = parseInt(value);
  }
  return value;
}

export function parseOverBet(overBet: any) {
  if (!!overBet && overBet.split('|').length > 1) {
    const overBetValues = overBet.split('|');

    return {
      seatId: +overBetValues[0],
      bet: +overBetValues[1]
    };
  }

  return {
    seatId: 0,
    bet: 0
  };
}

export function convertToNumber(val: any) {
  // this will never happen be an empty string
  if (isNaN(val) || val === null) {
    return 0;
  }
  // But, just want to make sure
  if (typeof val === 'string') {
    if (val.trim() === '') {
      return 0;
    }
  }

  const value = new Decimal(val).toDP(2).toNumber();

  // Check if the value has decimal places
  const stringValue = value.toString();

  // If there are decimal places, format to 2 decimal places
  if (stringValue.includes('.')) {
    return +value.toFixed(2);
  } else {
    // If it's a whole number, return the value without decimal places
    return +value.toFixed(0);
  }
}

export function calculateMaxTopUp(tablelimit: number, ppot: number, userBalance: number) {
  const allowedTopUp = Decimal.sub(tablelimit, ppot).toNumber();
  if (allowedTopUp <= 0) {
    return 0;
  }
  if (userBalance >= allowedTopUp) {
    return allowedTopUp;
  }
  if (userBalance < allowedTopUp) {
    return userBalance;
  }
  return 0;
}

// export function isWinner(runWinners: Run, seatId: number) {
//   for (const key in runWinners) {
//     const runWinner = (runWinners as any)[key];
//     if (!runWinner?.winTypes || !runWinner?.winners) {
//       continue;
//     }

//     if (runWinner.winTypes[runWinner.winners.indexOf(`${seatId}`)] === 'win') {
//       return true;
//     }
//   }

//   return false;
// }

export function getPlayerStatus(game: any, seatId: number) {
  /*
   *   Old reference https://github.com/Hijack-Poker/game/blob/main/texas/texasPlayer.js line 29
   *
   *   Player Status:
   *   0 = Seated
   *   1 = Sitout
   *   2 = Leave Game -- will kick the player after the game finishes
   *   3 = Show Cards
   *   4 = Join - wait for the button
   *   5 = Join - Buy the button
   *   6 =
   *   7 =
   *   8 = Show Cards
   */
  if (seatId < 1) {
    return {
      isWaiting: false
    };
  }

  if (game['p' + seatId + 'bet'].substring(0, 1) === 'J') {
    return {
      isWaiting: true
    };
  }

  switch (game['p' + seatId + 'status']) {
    case 4:
    case 5:
    case 9:
    case 10:
      // Join - wait for the button
      return {
        isWaiting: true
      };
    default:
      return {
        isWaiting: false
      };
  }
}

export function getPercentage(currentValue: number, targetValue: number) {
  return Math.min(Math.floor((currentValue / targetValue) * 100), 100);
}

export function isValidJsonString(str: string) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }

  return true;
}

export function getBetValue(player: Player) {
  if (!player.bet || player.bet.substr(0, 1) === 'J') {
    return 0;
  }

  return new Decimal(player.bet.replace('F', '')).toNumber();
}

export function secondsToTime(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const timeString = [hours, minutes, remainingSeconds]
    .map((value) => String(value).padStart(2, '0')) // Add leading zero if needed
    .join(':'); // Format as HH:MM:SS

  return timeString;
}

export function encode(str: string) {
  return encodeURIComponent(str)
    .replace(/-/g, '%2D')
    .replace(/_/g, '%5F')
    .replace(/\./g, '%2E')
    .replace(/!/g, '%21')
    .replace(/~/g, '%7E')
    .replace(/\*/g, '%2A')
    .replace(/'/g, '%27')
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29');
}

export function decode(str: string) {
  return decodeURIComponent(
    str
      .replace(/\\%2D/g, '-')
      .replace(/\\%5F/g, '_')
      .replace(/\\%2E/g, '.')
      .replace(/\\%21/g, '!')
      .replace(/\\%7E/g, '~')
      .replace(/\\%2A/g, '*')
      .replace(/\\%27/g, "'")
      .replace(/\\%28/g, '(')
      .replace(/\\%29/g, ')')
  );
}

export function toTitleCase(str: string) {
  const titleCase = str
    .toLowerCase()
    .split(' ')
    .map((word: string) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');

  return titleCase;
}

export function getTransformScale(width: number, height: number, minScale = 0.65) {
  // Reserve space for header (~60px) and bottom actions (~120px) before scaling the table.
  const headerHeight = 60;
  const footerHeight = 180;
  const availableHeight = height - headerHeight - footerHeight;

  // Table designed at OVERALL_WIDTH=1054, OVERALL_HEIGHT=470 (TABLE_HEIGHT=440, SEAT_DISTANCE=15)
  const tableBaseWidth = 1054;
  const tableBaseHeight = 470;

  const scaleX = width / tableBaseWidth;
  const scaleY = availableHeight / tableBaseHeight;

  return Math.min(Math.max(Math.min(scaleX, scaleY), minScale), 1.0);
}

export function getOrdinalSuffix(numbers: string) {
  const numberArray = String(numbers).split(',');

  const formattedNumbers = numberArray.map((number) => {
    let suffix = 'th';

    if (number === '1') {
      suffix = 'st';
    } else if (number === '2') {
      suffix = 'nd';
    } else if (number === '3') {
      suffix = 'rd';
    }

    return `${number}${suffix}`;
  });

  if (formattedNumbers.length === 1) {
    return formattedNumbers[0];
  }

  const lastNumber = formattedNumbers.pop();
  const formattedString = formattedNumbers.join(', ') + ', and ' + lastNumber;

  return formattedString;
}

export function formatTime(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  let formattedTime = '';
  if (hours > 0) {
    formattedTime += `${hours} hour${hours > 1 ? 's' : ''} `;
  }
  if (minutes > 0) {
    formattedTime += `${minutes} minute${minutes > 1 ? 's' : ''} `;
  }
  if (remainingSeconds > 0) {
    formattedTime += `${remainingSeconds} second${remainingSeconds > 1 ? 's' : ''}`;
  }

  return formattedTime.trim();
}

export function getLocalTime(timestamp: number, includeTime = true) {
  const date = new Date(timestamp * 1000);
  const ampm = date.getHours() >= 12 ? 'PM' : 'AM';

  // Format the date and time
  const formattedDate = `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  const formattedTime = `${date.getHours() % 12 || 12}:${String(date.getMinutes()).padStart(
    2,
    '0'
  )} ${ampm}`;

  return `${formattedDate} ${includeTime ? formattedTime : ''}`;
}

export function toDecimal(val: number, decimalPlaces = 6) {
  if (!val) {
    return new Decimal(0);
  }

  return new Decimal(val).toDP(decimalPlaces);
}

export function toLocaleStringNumber(val: any) {
  return Number(val).toLocaleString('en-US');
}

export function calculateCountdown(
  levelStarted: number,
  serverTime: number,
  countDownMinutes: number,
  nextLevel: number
) {
  const elapsedTime = serverTime - levelStarted;
  const remainingTime = countDownMinutes * 60 - elapsedTime;

  if (nextLevel !== -1 && remainingTime <= 0) {
    return '00:00:00'; // Countdown has reached zero
  } else if (nextLevel === -1 && remainingTime <= 0) {
    return 'Max Level';
  }

  return secondsToTime(remainingTime);
}

export function humanizeCountdown(seconds: number) {
  if (seconds === 0) {
    return 'Ended';
  }

  const timeUnits = [
    { label: 'day', value: Math.floor(seconds / 86400) },
    { label: 'hour', value: Math.floor((seconds % 86400) / 3600) },
    { label: 'min', value: Math.floor((seconds % 3600) / 60) },
    { label: 'sec', value: seconds % 60 }
  ];

  const parts = timeUnits
    .filter((unit) => unit.value !== 0)
    .map((unit) => `${unit.value} ${unit.label}${unit.value !== 1 ? 's' : ''}`);

  return parts.join(' ');
}

export function calculateStepValue(baseValue: number) {
  const magnitude = baseValue * 0.25;
  const validSteps = [0.01, 0.02, 0.05, 0.1, 0.2, 0.5];
  const closestStep = validSteps.reduce((acc, curr) => {
    return Math.abs(curr - magnitude) < Math.abs(acc - magnitude) ? curr : acc;
  });

  return closestStep;
}

export function isWinner(pots: any, showdown: any, seatId: number) {
  const pot = getWinnersPot(pots, showdown);

  return pot?.winners?.some(
    (f: any) => isHandTypeMatched(f, showdown?.handType) && +f.player === seatId && +f.win > 0
  );
}

export function isHighWinner(pots: any, showdown: any, seatId: number) {
  const pot = getWinnersPot(pots, showdown);

  return pot?.winners?.some(
    (f: any) =>
      isHandTypeMatched(f, showdown?.handType) &&
      +f.player === seatId &&
      +f.win > 0 &&
      (!f.handType || f.handType === HAND_TYPE.HI)
  );
}

export function isLowWinner(pots: any, showdown: any, seatId: number) {
  const pot = getWinnersPot(pots, showdown);

  return pot?.winners?.some(
    (f: any) =>
      isHandTypeMatched(f, showdown?.handType) &&
      +f.player === seatId &&
      +f.win > 0 &&
      f.handType === HAND_TYPE.LO
  );
}

export function isExecuteWinningHand(showdown: any) {
  const { currentStep } = showdown;

  if (+currentStep < 2) {
    return false;
  }

  return true;
}

/**
 * Whether this table's WINNING ANIMATION SEQUENCE has begun.
 *
 * One table-wide signal for "the hand is being won now", so anything that must
 * not survive into the winner announcement can be dismissed from a single place
 * instead of per seat.
 *
 * It fires on the EARLIEST beat of the sequence: the frame the payout data lands
 * at `hand >= FIND_WINNERS`, which is when the seat winner-pop plays and the
 * WINNER pill appears (`useSeatLogic.isWinner` and `shouldDimCard` read the same
 * signal — "as soon as `pots.winners` is populated and `hand >= 13`, not when
 * `showdown.currentStep` advances"). The confetti follows at `currentStep` 2
 * (POT_WINNER_DECLARATION_MIN_STEP) and the pot→seat chip flight at 3
 * (POT_CHIP_FLIGHT_MIN_STEP). Gating on the first beat rather than on the chip
 * flight is what makes "nothing persists INTO the winner announcement" true for
 * the whole sequence, not just for the payout.
 *
 * An all-fold win never produces a showdown to step through: the engine writes
 * `hand = PAY_WINNERS` and the winner in ONE atomic update (see the note on
 * `mapWinnersToArray` in reducers/currentTable), so there may be no populated
 * `pots[].winners` to wait for and the hand phase is the only signal available.
 *
 * `pots` reaches the client as a numeric-keyed OBJECT as readily as an array
 * (see `seatWinTotal` in hooks/useAnimatedWinStack, which switched to index
 * access for exactly this reason), so it is walked with `Object.values` —
 * `Array.prototype.some` would silently see nothing on the object form.
 */
export function hasWinnerAnimationStarted(game: any): boolean {
  const hand = Number(game?.hand);
  if (!Number.isFinite(hand) || hand < GAME_HAND.FIND_WINNERS) return false;
  // All-fold win / payout phase — no per-pot winners to wait for.
  if (hand >= GAME_HAND.PAY_WINNERS) return true;

  const pots = game?.pots;
  if (!pots || typeof pots !== 'object') return false;
  return Object.values(pots).some((pot: any) => Number(pot?.winners?.length) > 0);
}

export function potWinningHand(pots: any, showdown: any) {
  const { currentPot, handType } = showdown;

  if (!isExecuteWinningHand(showdown)) {
    return null;
  }

  return pots[currentPot]?.winners?.find(
    (player: any) => isHandTypeMatched(player, handType) && player?.hand
  )?.hand;
}

/**
 * The actual 5 cards making up the winning hand for the current pot reveal.
 * Returns an array of card strings (e.g. ['Qd', 'Qc', 'Qs', '10h', '4c']) or
 * an empty array when no winning hand is available to show.
 */
export function potWinningCards(pots: any, showdown: any): string[] {
  const { currentPot, handType } = showdown ?? {};

  if (!isExecuteWinningHand(showdown)) {
    return [];
  }

  const cards: string | undefined = pots?.[currentPot]?.winners?.find(
    (player: any) => isHandTypeMatched(player, handType) && player?.hand
  )?.cards;

  if (!cards) return [];

  return cards
    .split(',')
    .map((c: string) => c.trim())
    .filter(Boolean);
}

export function isWinningCard(pots: any, showdown: any, card: string) {
  const { currentPot, handType } = showdown;

  if (!isExecuteWinningHand(showdown)) {
    return false;
  }

  const pot = pots[currentPot] as { winners: any[] };

  return pot?.winners?.some((f) => isHandTypeMatched(f, handType) && f.cards.indexOf(card) > -1);
}

/**
 * Run-it-twice: the window where boards 2..N are still being DEALT, before any
 * winner is declared or any pot is paid (AC-6.3).
 *
 * The table sits at hand 13 for several ticks while the dealer runs those boards
 * out, `currentRun` still 1. But the broadcaster recomputes `pots[].winners` on
 * every frame from hand 13 on, so run 1's winning hand is already in the payload
 * while run 2 is still landing on the felt — and every "winner declared"
 * treatment read it: the losing board cards and hole cards dimmed, and the seat
 * winner pop fired, all before the last board was dealt. Nothing is declared
 * until the dealing stops.
 *
 * Bounded to hand 13 on purpose. Payouts run at hand 14, so this is false for
 * every one of them — including on the pre-deal's own fallback path
 * (MultiRunPreDealAbandoned), where the boards never arrive early and run 1 must
 * still light up normally.
 */
export function isMultiRunPreDeal(game: any): boolean {
  const runs = String(game?.runs ?? '').split(',');

  return (
    runs.length === 3 &&
    +runs[0] >= 2 &&
    +runs[2] === 1 &&
    +game?.hand === GAME_HAND_PHASE.FIND_WINNERS
  );
}

/**
 * Whether the board on the felt is the LAST one this hand will settle.
 *
 * `runs` is "<totalRuns>,<stage>,<currentRun>" while a run-it-twice/thrice
 * board is out, so the last board is the one where `currentRun` has caught up
 * with `totalRuns`. Any other `runs` value means there is a single board, which
 * is trivially the last one.
 *
 * The reducer's multi-run vote latch drops at this board's payout.
 */
export function isFinalRunOutBoard(game: any): boolean {
  const runs = String(game?.runs ?? '').split(',');
  return runs.length !== 3 || +runs[2] >= +runs[0];
}

/**
 * Whether a winner has visibly been DECLARED on the felt — the seat winner pop,
 * the dimmed losing cards, then the trophy pill and the chips.
 *
 * `hasWinnerAnimationStarted`, minus the run-it-twice pre-deal: board 1's
 * winner is in `pots` while boards 2..N are still being dealt, but nothing is
 * declared until the dealing stops (see `isMultiRunPreDeal`). The seat winner
 * pop (`useSeatLogic.isWinner`) is gated on exactly this, so anything keyed on
 * it goes on the same frame the declaration starts.
 */
export function hasWinnerDeclarationStarted(game: any): boolean {
  return hasWinnerAnimationStarted(game) && !isMultiRunPreDeal(game);
}

/**
 * Whether a run-it-twice/thrice hand has moved past board 1 — so board 1's
 * winner has already been declared, whatever the current frame carries.
 * `runs` is "<totalRuns>,<stage>,<currentRun>" during the run-out.
 */
export function isPastFirstRunOutBoard(game: any): boolean {
  const runs = String(game?.runs ?? '').split(',');
  return runs.length === 3 && +runs[2] > 1;
}

/**
 * Whether the given card should be visually dimmed because it is NOT part of
 * the winning hand. Synced with the seat winner-pop animation trigger
 * (see useSeatLogic.isWinner): activates as soon as `pots.winners` is
 * populated and `hand >= 13`, not when `showdown.currentStep` advances. Both
 * hold off through a run-it-twice pre-deal — see `isMultiRunPreDeal`.
 *
 * Returns false for all-folded hands (no showdown reveal happens).
 *
 * `isOtherRun` — run-it-twice only: this card sits on a board that is NOT the
 * one being paid right now (already settled, or pre-dealt and still to come).
 * Boards are pre-dealt at run 1's showdown (AC-6.3), so every run-out is on the
 * felt for the whole payout sequence; only the board being paid is lit, the
 * rest are dim, so the felt always says which run the money is moving for.
 *
 * It is answered BEFORE the gates below, not after, because those gates open
 * and close per board: taking the next run to its showdown puts `hand` back to
 * 5/7/9 and clears the winners off `pots`, which would light every other board
 * up again for the whole run-out. The gates describe the ONE board being
 * settled right now — that is also why another board's cards must not be
 * matched against `pots`/`showdown` at all: whichever way the two disagree the
 * felt ends up lighting a winning hand that is not the current run's, which is
 * the reported bug. Each board keeps its won-accent in HoldEmCards, and the
 * hand history holds the payouts, so nothing about who won it is lost.
 */
export function shouldDimCard(game: any, card: string, isOtherRun = false): boolean {
  if (isMultiRunPreDeal(game)) return false;
  if (isOtherRun) return true;

  if (game?.languageKey === 'GAME_MSG_ALLFOLD') return false;
  // Hand-history snapshots opt into concluded-hand card highlighting without
  // mutating the live table's hand phase.
  if (+game?.hand < 13 && !game?.handHistorySnapshot) return false;

  const pots = game?.pots;
  if (!pots?.some((p: any) => p?.winners?.length > 0)) return false;

  const { currentPot, handType } = game?.showdown ?? {};
  const inCurrentPot = pots[currentPot]?.winners?.some(
    (f: any) => isHandTypeMatched(f, handType) && f.cards.indexOf(card) > -1
  );
  if (inCurrentPot) return false;

  if (game?.handHistorySnapshot) {
    const inAnyPot = pots.some((p: any) =>
      p?.winners?.some((f: any) => isHandTypeMatched(f, handType) && f.cards.indexOf(card) > -1)
    );
    return !inAnyPot;
  }

  return true;
}

export function playerPotWin(pots: any, showdown: any, seatId: number) {
  const { currentPot, currentStep, handType } = showdown;

  // From the winner step (2), with the WINNER badge and the confetti, not the
  // chips step (3): the pot's winners and amounts are already in the frame, and
  // the three celebrate together while the chips still fly at step 3. EN-10985.
  if (+currentStep < 2) {
    return 0;
  }

  const pot = pots[currentPot] as { winners: any[] };

  return (
    pot?.winners
      ?.filter((f) => isHandTypeMatched(f, handType) && +f.player === seatId)
      .map((f) => +f.win)
      .reduce((total, num) => total + num, 0) ?? 0
  );
}

function getWinnersPot(pots: any, showdown: any): any {
  const { currentPot, currentStep } = showdown;

  if (+currentStep < 2) {
    return false;
  }

  return pots[currentPot] as { winners: any[] };
}

function isHandTypeMatched(player: any, handType: string) {
  if (!handType || !player.handType) {
    return true;
  }

  return player.handType === handType;
}

function isJsonObject(data: any) {
  try {
    const parsed = JSON.parse(data);
    return typeof parsed === 'object' && parsed !== null;
  } catch (e) {
    return false; // Not valid JSON or not an object
  }
}

export function decompressData(data: any) {
  // Time every call so the PerfHud can surface decompression CPU cost — the
  // main suspect for idle heat (a chatty socket inflating + parsing payloads
  // on the main thread for tables you aren't even looking at).
  const start = performance.now();
  try {
    return decompressDataInner(data);
  } finally {
    recordDecompress(performance.now() - start);
  }
}

function decompressDataInner(data: any) {
  // If data is already an object or non-base64 string, return it as-is (uncompressed mode)
  if (typeof data === 'object' && data !== null) {
    return data;
  }

  // Check if it's a valid base64 string
  try {
    const binaryData = atob(data);
    const uint8Array = Uint8Array.from(binaryData, (char) => char.charCodeAt(0));
    const decompressedData = pako.ungzip(uint8Array, { to: 'string' });

    if (isJsonObject(decompressedData)) return JSON.parse(decompressedData);

    return decompressedData;
  } catch (e) {
    // If decompression fails, assume it's uncompressed data
    if (isJsonObject(data)) return JSON.parse(data);
    return data;
  }
}
